import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axiosClient from '../api/axiosClient';

const STORAGE_KEY = 'bct_selected_currency';

export interface CurrencyInfo {
  code: string;
  name: string;
  symbol: string;
}

interface CurrencyContextValue {
  currencies: CurrencyInfo[];
  selected: string;
  setSelected: (code: string) => void;
  symbol: string;
  loading: boolean;
  /** Convert an amount that is denominated in INR into the selected currency. */
  convertFromInr: (amountInInr: number) => number;
  /** Convert an amount that is denominated in USD into the selected currency. */
  convertFromUsd: (amountInUsd: number) => number;
  /** Convert an amount denominated in ANY given currency (e.g. a funds_request row's own currency). */
  convertFromNative: (amount: number, nativeCurrency: string) => number;
  /** Convert any native-currency amount directly to INR — independent of the display currency selection. */
  toInr: (amount: number, nativeCurrency: string) => number;
  /**
   * Display a transaction consistently with totals/wallet balance: shows the exact original
   * amount when viewing in its own native currency, the exact stored INR-equivalent when
   * viewing in INR, and only derives a live conversion for a third currency. This never
   * disagrees with a Total/Wallet figure computed from the same amountInr values.
   */
  /**
   * Convert a transaction consistently with formatTransaction, returning a plain number.
   * Use this to build totals by summing the SAME per-row values that get individually
   * displayed, so a Total figure can never disagree with the sum of the rows shown below it.
   */
  convertTransaction: (nativeAmount: number, nativeCurrency: string, amountInr: number) => number;
  formatTransaction: (nativeAmount: number, nativeCurrency: string, amountInr: number) => string;
  /** How many INR equal 1 unit of the selected currency, right now — for a transparency label
   *  next to converted totals (e.g. "1 USD = ₹83.50"), so users understand where a number came from. */
  inrPerSelected: number | null;
  /** How many INR equal 1 unit of a given currency, right now — for per-transaction rate labels. */
  inrPerUnit: (currency: string) => number | null;
  /** Format an amount that is ALREADY in the selected currency — no further conversion. */
  format: (amount: number) => string;
  /** Convert + format with the correct symbol, e.g. "$1,234" or "₹1,00,000". */
  formatFromInr: (amountInInr: number) => string;
  formatFromUsd: (amountInUsd: number) => string;
  formatFromNative: (amount: number, nativeCurrency: string) => string;
}

const CurrencyContext = createContext<CurrencyContextValue>({
  currencies: [],
  selected: 'INR',
  setSelected: () => {},
  symbol: '₹',
  loading: true,
  convertFromInr: (a) => a,
  convertFromUsd: (a) => a,
  convertFromNative: (a) => a,
  toInr: (a) => a,
  convertTransaction: (a) => a,
  formatTransaction: (a) => `₹${a.toLocaleString()}`,
  inrPerSelected: null,
  inrPerUnit: () => null,
  format: (a) => `₹${a.toLocaleString()}`,
  formatFromInr: (a) => `₹${a.toLocaleString()}`,
  formatFromUsd: (a) => `$${a.toLocaleString()}`,
  formatFromNative: (a) => `₹${a.toLocaleString()}`,
});

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currencies, setCurrencies] = useState<CurrencyInfo[]>([]);
  const [rates, setRates] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelectedState] = useState<string>(() => localStorage.getItem(STORAGE_KEY) || 'INR');

  useEffect(() => {
    Promise.all([
      axiosClient.get('/currency/list'),
      axiosClient.get('/currency/rates'),
    ]).then(([listRes, ratesRes]) => {
      setCurrencies(listRes.data.currencies || []);
      setRates(ratesRes.data.rates || {});
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const setSelected = useCallback((code: string) => {
    setSelectedState(code);
    localStorage.setItem(STORAGE_KEY, code);
  }, []);

  const convertFromUsd = useCallback((amountInUsd: number) => {
    const rate = rates[selected];
    if (!rate || !Number.isFinite(amountInUsd)) return amountInUsd;
    return amountInUsd * rate;
  }, [rates, selected]);

  const convertFromInr = useCallback((amountInInr: number) => {
    const rateInr = rates.INR;
    if (!rateInr || !Number.isFinite(amountInInr)) return amountInInr;
    const amountInUsd = amountInInr / rateInr;
    return convertFromUsd(amountInUsd);
  }, [rates, convertFromUsd]);

  // Convert an amount that is denominated in ANY given currency (e.g. a deposit that was
  // submitted in USD or EUR) into the selected display currency. This is the correct function
  // to use for funds_requests rows, which store a `currency` alongside the `amount` — using
  // convertFromInr on those would silently mistreat a $500 deposit as ₹500.
  const convertFromNative = useCallback((amount: number, nativeCurrency: string) => {
    const nativeRate = rates[nativeCurrency];
    if (!nativeRate || !Number.isFinite(amount)) return amount;
    const amountInUsd = amount / nativeRate;
    return convertFromUsd(amountInUsd);
  }, [rates, convertFromUsd]);

  const toInr = useCallback((amount: number, nativeCurrency: string) => {
    const nativeRate = rates[nativeCurrency];
    const inrRate = rates.INR;
    if (!nativeRate || !inrRate || !Number.isFinite(amount)) return amount;
    const amountInUsd = amount / nativeRate;
    return amountInUsd * inrRate;
  }, [rates]);

  const symbol = currencies.find((c) => c.code === selected)?.symbol || selected;

  const formatAmount = useCallback((amount: number) => {
    if (loading) return '…';
    const rounded = Math.round(amount * 100) / 100;
    return `${symbol}${rounded.toLocaleString(undefined, { maximumFractionDigits: rounded % 1 === 0 ? 0 : 2 })}`;
  }, [symbol, loading]);

  const formatFromInr = useCallback((amountInInr: number) => formatAmount(convertFromInr(amountInInr)), [convertFromInr, formatAmount]);
  const formatFromUsd = useCallback((amountInUsd: number) => formatAmount(convertFromUsd(amountInUsd)), [convertFromUsd, formatAmount]);
  const formatFromNative = useCallback(
    (amount: number, nativeCurrency: string) => formatAmount(convertFromNative(amount, nativeCurrency)),
    [convertFromNative, formatAmount]
  );

  // Viewing in the transaction's own native currency → use the exact original amount, no
  // conversion at all. Viewing in INR → use the exact stored INR-equivalent (same value the
  // Total/Wallet figures are built from). Any other currency → derive from that same stable
  // INR-equivalent. Totals MUST sum this exact function's output per row (not re-derive some
  // other way), or a Total can silently disagree with the sum of the rows shown below it.
  const convertTransaction = useCallback((nativeAmount: number, nativeCurrency: string, amountInr: number) => {
    if (selected === nativeCurrency) return nativeAmount;
    if (selected === 'INR') return amountInr;
    return convertFromInr(amountInr);
  }, [selected, convertFromInr]);

  const formatTransaction = useCallback((nativeAmount: number, nativeCurrency: string, amountInr: number) => {
    if (loading) return '…';
    return formatAmount(convertTransaction(nativeAmount, nativeCurrency, amountInr));
  }, [loading, formatAmount, convertTransaction]);

  const inrPerUnit = useCallback((currency: string) => {
    const rate = rates[currency];
    const inrRate = rates.INR;
    if (!rate || !inrRate) return null;
    return inrRate / rate;
  }, [rates]);

  const inrPerSelected = inrPerUnit(selected);

  return (
    <CurrencyContext.Provider
      value={{
        currencies, selected, setSelected, symbol, loading,
        convertFromInr, convertFromUsd, convertFromNative, toInr,
        formatFromInr, formatFromUsd, formatFromNative, convertTransaction, formatTransaction,
        inrPerSelected, inrPerUnit, format: formatAmount,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export function useCurrency() {
  return useContext(CurrencyContext);
}
